import { Chip } from '@ori-ui/mui';
import { isPpaLabel } from '@ori/presentation-components';
import useTestId from '@ori/testid-generator';
import { useMemo, type FC } from 'react';

import { type SearchPreviewProductProps } from '../../types';
import { allowedDealLabels, allowedNegativePpaLabels, SEARCH_PREVIEW_PRODUCT_PRODUCT_INFO_LABEL } from './constants';
import { LabelContainer } from './styles';

export type ProductInfoLabelProps = Pick<SearchPreviewProductProps, 'ppa' | 'deal' | 'code'>;

export const ProductInfoLabel: FC<ProductInfoLabelProps> = ({ ppa, deal, code }) => {
  const { getTestId } = useTestId();
  const shouldDisplayPpaLabel = useMemo(() => isPpaLabel(ppa?.labelKey) && !!ppa.title, [ppa]);
  const shouldDisplayDealLabel = useMemo(
    () => !shouldDisplayPpaLabel && allowedDealLabels.includes(deal?.labelKey ?? '') && !!deal?.title,
    [deal, shouldDisplayPpaLabel],
  );
  // TODO export isNegativePpaLabel from presentation-components and use it here
  const isNegative = useMemo(
    () => shouldDisplayPpaLabel && allowedNegativePpaLabels.includes(ppa?.labelKey ?? ''),
    [shouldDisplayPpaLabel, ppa?.labelKey],
  );

  if (!shouldDisplayPpaLabel && !shouldDisplayDealLabel) {
    return null;
  }

  if (shouldDisplayPpaLabel) {
    return (
      <LabelContainer>
        <Chip
          data-testid={getTestId(`${SEARCH_PREVIEW_PRODUCT_PRODUCT_INFO_LABEL}-${code}`)}
          label={ppa?.title}
          variant="tag"
          color={isNegative ? 'error' : 'default'}
        />
      </LabelContainer>
    );
  }

  return (
    <LabelContainer>
      <Chip
        data-testid={getTestId(`${SEARCH_PREVIEW_PRODUCT_PRODUCT_INFO_LABEL}-${code}`)}
        label={deal?.title}
        variant="filled"
        color="default"
        size="small"
      />
    </LabelContainer>
  );
};
